/**
 * HYPSEYA — Window of Tolerance
 * Clinical visualization of nervous system regulatory state.
 * Based on: Siegel (1999), Ogden (2006), Levine (1997)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Wave, ArrowLeft, ArrowUp, ArrowDown } from '@/components/HypIcons';
import { HRVCard } from '@/hooks/useHealthKit';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Clinical zone definitions ──────────────────────────────────────────────
const ZONES = [
  {
    id: 'hyper',
    label: 'Hyper-arousal',
    sublabel: 'Sympathetic activation',
    range: [7, 10],
    color: '#B8906A',
    bgColor: 'rgba(184,144,106,0.10)',
    borderColor: 'rgba(184,144,106,0.3)',
    description: 'Fight / flight / freeze. The system perceives threat. Anxiety, agitation, reactivity, overwhelm. Difficult to learn, integrate or regulate.',
    markers: ['Anxiety', 'Reactivity', 'Racing thoughts', 'Overwhelm', 'Hypervigilance']
  },
  {
    id: 'window',
    label: 'Window of Tolerance',
    sublabel: 'Regulated state — optimal for learning',
    range: [3, 6],
    color: '#1E6B68',
    bgColor: 'rgba(30,107,104,0.07)',
    borderColor: 'rgba(30,107,104,0.3)',
    description: 'The nervous system is regulated. This is where emotional processing, learning, integration, and genuine change occur. The goal of all practice.',
    markers: ['Presence', 'Curiosity', 'Groundedness', 'Capacity', 'Flexibility']
  },
  {
    id: 'hypo',
    label: 'Hypo-arousal',
    sublabel: 'Dorsal vagal withdrawal',
    range: [1, 2],
    color: '#7A9BA8',
    bgColor: 'rgba(122,155,168,0.10)',
    borderColor: 'rgba(122,155,168,0.3)',
    description: 'Shutdown, freeze, collapse, dissociation. The system has withdrawn from perceived overwhelm. Numbness, fatigue, fog, disconnection.',
    markers: ['Numbness', 'Disconnection', 'Fatigue', 'Fog', 'Shutdown']
  }
];

function getZone(activation) {
  if (activation >= 7) return ZONES[0];
  if (activation >= 3) return ZONES[1];
  return ZONES[2];
}

// ── Nervous system map — vertical column ──────────────────────────────────
function NervousSystemMap({ currentActivation, historicalRange, windowWidth }) {
  const zone = getZone(currentActivation);
  // Map activation 1-10 to percentage 0-100 (inverted: 10 at top)
  const posY = 100 - ((currentActivation - 1) / 9) * 100;

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 340, margin: '0 auto' }}>
      {/* Zone bars */}
      {ZONES.map((z, i) => {
        const heightPct = i === 1 ? 46 : 27; // window bigger
        const topPct    = i === 0 ? 0 : i === 1 ? 27 : 73;
        const isActive  = z.id === zone.id;
        return (
          <div key={z.id} style={{
            position: 'relative',
            minHeight: `${heightPct * 2.2}px`,
            background: isActive ? z.bgColor : 'rgba(255,255,255,0.5)',
            border: `1px solid ${isActive ? z.borderColor : 'rgba(184,211,207,0.25)'}`,
            borderRadius: i === 0 ? '16px 16px 0 0' : i === 2 ? '0 0 16px 16px' : '0',
            padding: '12px 16px',
            transition: 'background 0.4s ease',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div>
              <div style={{
                fontSize: '0.8rem', fontWeight: '700', color: z.color,
                letterSpacing: '0.04em', marginBottom: '2px'
              }}>{z.label}</div>
              <div style={{ fontSize: '0.7rem', color: '#AAB8B6', fontStyle: 'italic' }}>
                {z.sublabel}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                {z.markers.map(m => (
                  <span key={m} style={{
                    fontSize: '0.65rem', background: `${z.color}18`,
                    color: z.color, padding: '2px 7px', borderRadius: '8px',
                    fontWeight: '500'
                  }}>{m}</span>
                ))}
              </div>
            </div>
            {isActive && (
              <div style={{
                flexShrink: 0,
                width: 28, height: 28, borderRadius: '50%',
                background: z.color, boxShadow: `0 0 0 6px ${z.color}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
              </div>
            )}
          </div>
        );
      })}

      {/* Historical range band */}
      {historicalRange && (
        <div style={{
          position: 'absolute', right: -28, top: 0, bottom: 0,
          width: 14, display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}>
          <div style={{ fontSize: '0.6rem', color: '#B8D3CF', marginBottom: 2, whiteSpace: 'nowrap', writingMode: 'vertical-lr' }}>
            typical range
          </div>
          <div style={{
            flex: 1, width: 4, background: 'rgba(184,211,207,0.25)', borderRadius: 2,
            position: 'relative'
          }}>
            {/* Range band */}
            <div style={{
              position: 'absolute',
              top:    `${100 - ((historicalRange.max - 1) / 9) * 100}%`,
              bottom: `${((historicalRange.min - 1) / 9) * 100}%`,
              left: 0, right: 0,
              background: 'rgba(45,139,135,0.35)', borderRadius: 2
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function WindowOfTolerance() {
  const navigate = useNavigate();
  const [user, setUser]               = useState(null);
  const [eriData, setEriData]         = useState(null);
  const [recentSessions, setRecent]   = useState([]);
  const [selectedZone, setSelected]   = useState(null);
  const [isVisible, setIsVisible]     = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 80);
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;
    const load = async () => {
      try {
        const [eriRes, histRes] = await Promise.allSettled([
          axios.get(`${API}/training/eri/${user.user_id}`),
          axios.get(`${API}/training/eri-history/${user.user_id}?limit=14`)
        ]);
        if (eriRes.status === 'fulfilled')  setEriData(eriRes.value.data);
        if (histRes.status === 'fulfilled') setRecent(histRes.value.data.scores || []);
      } catch {}
    };
    load();
  }, [user]);

  // Derive current activation from avg_pre_activation or ERI score
  const currentActivation = eriData?.avg_pre_activation
    ? Math.round(eriData.avg_pre_activation)
    : eriData?.eri_score
      ? Math.round(10 - (eriData.eri_score / 100) * 7) // invert: high ERI = lower activation
      : 5;

  // Historical range from sessions
  const activations = recentSessions
    .map(s => s.pre_activation)
    .filter(Boolean);
  const historicalRange = activations.length >= 3 ? {
    min: Math.min(...activations),
    max: Math.max(...activations),
    avg: Math.round(activations.reduce((a,b) => a+b, 0) / activations.length)
  } : null;

  const currentZone = getZone(currentActivation);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.18) 100%)',
      padding: '28px 20px 100px',
      maxWidth: '680px', margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease'
    }}>

      {/* Back */}
      <button onClick={() => navigate(-1)} style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#8FA3A0', fontSize: '0.82rem', marginBottom: '24px',
        padding: 0
      }}>
        <ArrowLeft size={14} color="#8FA3A0" /> Back
      </button>

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <Wave size={22} color="#2D8B87" />
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.6rem', color: '#1E3A39',
            margin: 0, fontWeight: '500'
          }}>Window of Tolerance</h1>
        </div>
        <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.85rem', lineHeight: '1.6' }}>
          Your nervous system's regulatory range — where you are, where you've been.
        </p>
      </div>

      {/* Current state card */}
      <div style={{
        background: '#fff', borderRadius: '20px',
        padding: '24px 28px', marginBottom: '24px',
        boxShadow: '0 2px 20px rgba(30,107,104,0.07)',
        borderLeft: `3px solid ${currentZone.color}`
      }}>
        <div style={{
          fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em',
          textTransform: 'uppercase', fontWeight: '600', marginBottom: '12px'
        }}>
          Current state — based on recent sessions
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: currentZone.color,
            boxShadow: `0 0 0 8px ${currentZone.color}20`,
            flexShrink: 0
          }} />
          <div>
            <div style={{ fontSize: '1.2rem', fontWeight: '700', color: currentZone.color, fontFamily: "'Playfair Display', serif" }}>
              {currentZone.label}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#AAB8B6', fontStyle: 'italic' }}>
              {currentZone.sublabel}
            </div>
          </div>
        </div>
        <p style={{ margin: 0, fontSize: '0.88rem', color: '#555', lineHeight: '1.65' }}>
          {currentZone.description}
        </p>
      </div>

      {/* Map */}
      <div style={{
        background: '#fff', borderRadius: '20px',
        padding: '28px 24px', marginBottom: '24px',
        boxShadow: '0 2px 20px rgba(30,107,104,0.07)'
      }}>
        <div style={{
          fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em',
          textTransform: 'uppercase', fontWeight: '600', marginBottom: '20px'
        }}>
          Nervous system map
        </div>
        <NervousSystemMap
          currentActivation={currentActivation}
          historicalRange={historicalRange}
        />
        {historicalRange && (
          <p style={{
            margin: '16px 0 0', fontSize: '0.78rem', color: '#AAB8B6',
            textAlign: 'center', fontStyle: 'italic'
          }}>
            Your typical activation range: {historicalRange.min}–{historicalRange.max} (avg {historicalRange.avg})
          </p>
        )}
        {!historicalRange && (
          <p style={{
            margin: '16px 0 0', fontSize: '0.78rem', color: '#AAB8B6',
            textAlign: 'center', fontStyle: 'italic'
          }}>
            Complete more sessions to see your personal range.
          </p>
        )}
      </div>

      {/* The science — expandable zone cards */}
      <div style={{
        background: '#fff', borderRadius: '20px',
        padding: '24px 28px', marginBottom: '24px',
        boxShadow: '0 2px 20px rgba(30,107,104,0.07)'
      }}>
        <div style={{
          fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em',
          textTransform: 'uppercase', fontWeight: '600', marginBottom: '16px'
        }}>
          Understanding the zones
        </div>
        {ZONES.map(z => (
          <div
            key={z.id}
            onClick={() => setSelected(selectedZone === z.id ? null : z.id)}
            style={{
              padding: '14px 16px', borderRadius: '12px', marginBottom: '8px',
              background: selectedZone === z.id ? z.bgColor : 'rgba(247,243,236,0.6)',
              border: `1px solid ${selectedZone === z.id ? z.borderColor : 'transparent'}`,
              cursor: 'pointer', transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: '600', color: z.color }}>{z.label}</span>
              <span style={{ fontSize: '0.7rem', color: '#B8D3CF' }}>{selectedZone === z.id ? '▲' : '▼'}</span>
            </div>
            {selectedZone === z.id && (
              <p style={{
                margin: '10px 0 0', fontSize: '0.85rem',
                color: '#555', lineHeight: '1.65'
              }}>{z.description}</p>
            )}
          </div>
        ))}
      </div>

      {/* Apple Health / HRV */}
      <div style={{ marginBottom: '24px' }}>
        <HRVCard compact={false} />
      </div>

      {/* Clinical note */}
      <div style={{
        padding: '20px 24px', borderRadius: '16px',
        background: 'rgba(30,107,104,0.04)',
        border: '1px solid rgba(184,211,207,0.3)'
      }}>
        <p style={{
          margin: 0, fontSize: '0.8rem', color: '#8FA3A0', lineHeight: '1.7',
          fontStyle: 'italic'
        }}>
          The Window of Tolerance model was developed by neuropsychiatrist Daniel J. Siegel (1999)
          and expanded by Pat Ogden, Janina Fisher, and Peter Levine in the context of
          somatic and trauma-informed therapy. The goal of all HYPSEYA practice is to
          widen your window — increasing your capacity to remain regulated under increasing
          intensity of experience.
        </p>
      </div>
    </div>
  );
}
